// src/services/ttsService.js
import offlineService from './offlineService';

class TTSService {
  constructor() {
    this.cache = new Map();
    this.isOnline = navigator.onLine;
    this.fallbackVoices = null;
    this._piper = null; // lazy-loaded @mintplex-labs/piper-tts-web
    this._activeAudio = null;

    // Listen for online/offline status
    window.addEventListener('online', () => {
      this.isOnline = true;
    });
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });

    // Initialize fallback voices when available
    if ('speechSynthesis' in window) {
      // Chrome/Edge load voices async; do both: event + a few timed retries.
      const onVoicesChanged = () => this.initializeFallbackVoices();
      speechSynthesis.addEventListener('voiceschanged', onVoicesChanged);
      setTimeout(() => this.initializeFallbackVoices(), 50);
      setTimeout(() => this.initializeFallbackVoices(), 250);
      setTimeout(() => this.initializeFallbackVoices(), 1000);
    }
  }

  initializeFallbackVoices() {
    const voices = speechSynthesis.getVoices();
    const isDev = typeof window !== 'undefined' && (window.location?.hostname === 'localhost' || window.location?.hostname === '127.0.0.1');

    // Find best German voices with priority order
    const germanVoices = voices.filter(voice => voice.lang.startsWith('de'));

    this.fallbackVoices = {
      // Priority 1: High-quality German voices
      female_primary: germanVoices.find(voice =>
        voice.name.includes('Neural') ||
        voice.name.includes('Natural') ||
        voice.name.includes('Google') ||
        voice.name.includes('Microsoft') && voice.name.toLowerCase().includes('female')
      ) || germanVoices.find(voice => voice.name.toLowerCase().includes('female')),

      male_primary: germanVoices.find(voice =>
        voice.name.includes('Neural') ||
        voice.name.includes('Natural') ||
        voice.name.includes('Google') ||
        voice.name.includes('Microsoft') && voice.name.toLowerCase().includes('male')
      ) || germanVoices.find(voice => voice.name.toLowerCase().includes('male')),

      // Fallback: Any German voice
      primary: germanVoices.find(voice =>
        voice.lang === 'de-DE' &&
        (voice.name.includes('Neural') || voice.name.includes('Natural'))
      ) || germanVoices.find(voice => voice.lang === 'de-DE'),

      secondary: germanVoices.find(voice =>
        voice.lang.startsWith('de') &&
        voice.name.includes('Google')
      ) || germanVoices[0], // Any German voice as last resort

      // Last resort: any voice (e.g. Chrome Linux with no German pack)
      any: voices[0]
    };

    if (isDev && (!this.fallbackVoices.primary && !this.fallbackVoices.secondary)) {
      console.warn(
        'TTS: No German Web Speech voice found. Falling back to on-device Piper if needed. Voices:',
        voices.length,
        voices.map(v => `${v.name} (${v.lang})`)
      );
    }
  }

  async speak(text, options = {}) {
    const {
      priority = 'normal',
      voice = 'female_clear',
      speed = 0.85,
      cache = true
    } = options;

    const normalizedText = String(text || '').trim();
    if (!normalizedText) return;

    // Cancel any in-progress audio (WebSpeech or Audio element) so taps feel immediate.
    this.stop();

    // Primary: Web Speech API (free, instant when voices exist)
    try {
      if ('speechSynthesis' in window) {
        const voices = speechSynthesis.getVoices();
        // On many Linux setups Chromium reports 0 voices permanently.
        if (Array.isArray(voices) && voices.length > 0) {
          return await this.fallbackToWebSpeech(normalizedText, { voice, speed });
        }
      }
    } catch (error) {
      // If WebSpeech exists but fails (some devices), fall through to Piper.
      const isDev = typeof window !== 'undefined' && (window.location?.hostname === 'localhost' || window.location?.hostname === '127.0.0.1');
      if (isDev) console.warn('TTS: Web Speech failed, trying Piper fallback:', error?.message || error);
    }

    // Fallback: Piper TTS in the browser (free, high quality, works offline after first model download)
    try {
      return await this.fallbackToPiper(normalizedText, { voice, speed, cache, priority });
    } catch (error) {
      console.warn('TTS unavailable:', error?.message || error);
      throw error;
    }
  }

  async fallbackToWebSpeech(text, options) {
    if (!('speechSynthesis' in window)) {
      throw new Error('Speech synthesis not supported');
    }

    // Ensure voices are loaded (Chrome loads them async; on Linux they may not be ready yet)
    if (!this.fallbackVoices) {
      this.initializeFallbackVoices();
    }
    // If still no voices after a tick, wait for onvoiceschanged once
    if (!this.fallbackVoices?.primary && !this.fallbackVoices?.secondary) {
      await new Promise((resolve) => {
        const done = () => {
          speechSynthesis.removeEventListener('voiceschanged', done);
          this.initializeFallbackVoices();
          resolve();
        };
        speechSynthesis.addEventListener('voiceschanged', done);
        setTimeout(done, 500);
      });
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'de-DE';
    utterance.rate = options.speed || 0.9;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    // Select voice based on content type and preference
    let selectedVoice = null;
    if (this.fallbackVoices) {
      if (options.voice === 'female_clear' || options.voice?.includes('female')) {
        selectedVoice = this.fallbackVoices.female_primary || this.fallbackVoices.primary;
      } else if (options.voice === 'male_clear' || options.voice?.includes('male')) {
        selectedVoice = this.fallbackVoices.male_primary || this.fallbackVoices.primary;
      } else {
        selectedVoice = this.fallbackVoices.primary || this.fallbackVoices.secondary;
      }
      // Last resort: use any voice (e.g. Linux with no German pack) so lang still applies
      if (!selectedVoice && this.fallbackVoices.any) {
        selectedVoice = this.fallbackVoices.any;
      }
    }

    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }
    // If still no voice (getVoices() empty), speaking will likely fail; throw so caller can show a message
    if (!selectedVoice && speechSynthesis.getVoices().length === 0) {
      throw new Error('No TTS voice available on this device. Try again online for cloud pronunciation.');
    }

    // Optimize parameters for German pronunciation
    utterance.rate = Math.max(0.7, Math.min(1.0, utterance.rate));

    // Adjust pitch based on voice gender for better German pronunciation
    if (utterance.voice?.name) {
      const voiceName = utterance.voice.name.toLowerCase();
      if (voiceName.includes('female') || voiceName.includes('zira') || voiceName.includes('hedda')) {
        utterance.pitch = 1.1; // Slightly higher pitch for female voices
      } else if (voiceName.includes('male') || voiceName.includes('david') || voiceName.includes('stefan')) {
        utterance.pitch = 0.9; // Slightly lower pitch for male voices
      }
    }

    // Cancel any in-progress speech to avoid "interrupted" errors
    speechSynthesis.cancel();
    await new Promise(resolve => setTimeout(resolve, 80));

    return new Promise((resolve) => {
      utterance.onend = () => resolve();

      utterance.onerror = (event) => {
        // "interrupted" just means we cancelled a previous utterance, not a real error
        if (event.error === 'interrupted' || event.error === 'canceled') {
          resolve();
        } else {
          console.warn('Web Speech error:', event.error);
          resolve(); // resolve anyway so the UI isn't blocked
        }
      };

      speechSynthesis.speak(utterance);
    });
  }

  stop() {
    try {
      if ('speechSynthesis' in window) speechSynthesis.cancel();
    } catch (_) {
      // ignore
    }
    if (this._activeAudio) {
      try {
        this._activeAudio.pause();
        this._activeAudio.src = '';
      } catch (_) {
        // ignore
      }
      this._activeAudio = null;
    }
  }

  _hashText(input) {
    // Fast, stable (non-crypto) hash to keep cache keys compact.
    const str = String(input || '');
    let h = 2166136261;
    for (let i = 0; i < str.length; i += 1) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return (h >>> 0).toString(16);
  }

  _piperVoiceId(voiceKey = 'female_clear') {
    // German voices in @mintplex-labs/piper-tts-web:
    //   Female: de_DE-kerstin-low, de_DE-ramona-low, de_DE-eva_k-x_low
    //   Male:   de_DE-thorsten-high (best quality), de_DE-thorsten-medium, de_DE-karlsson-low
    const map = {
      female_clear: 'de_DE-kerstin-low',
      female_warm: 'de_DE-ramona-low',
      male_clear: 'de_DE-thorsten-high',
      male_warm: 'de_DE-thorsten-medium',
    };
    return map[voiceKey] || 'de_DE-thorsten-high';
  }

  async _loadPiper() {
    if (this._piper) return this._piper;
    // Lazy-load only when Web Speech has no voices.
    const mod = await import('@mintplex-labs/piper-tts-web');
    this._piper = mod;
    return mod;
  }

  async _playBlob(blob, { playbackRate = 1 } = {}) {
    const audio = new Audio();
    audio.src = URL.createObjectURL(blob);
    audio.preload = 'auto';
    audio.playbackRate = Math.max(0.7, Math.min(1.15, playbackRate));
    this._activeAudio = audio;

    return new Promise((resolve, reject) => {
      audio.onended = () => {
        try { URL.revokeObjectURL(audio.src); } catch (_) {}
        if (this._activeAudio === audio) this._activeAudio = null;
        resolve();
      };
      audio.onerror = (e) => {
        try { URL.revokeObjectURL(audio.src); } catch (_) {}
        if (this._activeAudio === audio) this._activeAudio = null;
        reject(e);
      };
      audio.play().catch(reject);
    });
  }

  async fallbackToPiper(text, options) {
    const piper = await this._loadPiper();
    const voiceId = this._piperVoiceId(options.voice);
    const speed = typeof options.speed === 'number' ? options.speed : 0.85;

    const cacheKey = `tts:piper:${voiceId}:${Math.round(speed * 100)}:${this._hashText(text)}`;
    if (options.cache !== false) {
      try {
        const cached = await offlineService.getCachedContent(cacheKey);
        if (cached?.audioBuffer) {
          const blob = new Blob([cached.audioBuffer], { type: cached.mime || 'audio/wav' });
          return await this._playBlob(blob, { playbackRate: speed });
        }
      } catch (_) {
        // ignore cache failures
      }
    }

    // Generate wav (library downloads model to OPFS on first run).
    const wavBlob = await piper.predict({ text, voiceId }, () => {});

    // Cache the generated audio to make repeat taps instant, even offline.
    if (options.cache !== false) {
      try {
        const buf = await wavBlob.arrayBuffer();
        await offlineService.cacheContent(cacheKey, { audioBuffer: buf, mime: 'audio/wav' }, 'tts');
      } catch (_) {
        // ignore cache failures
      }
    }

    return await this._playBlob(wavBlob, { playbackRate: speed });
  }

  async playAudio(audioUrl) {
    return new Promise((resolve, reject) => {
      const audio = new Audio(audioUrl);
      audio.onended = () => resolve();
      audio.onerror = (error) => reject(error);
      audio.play().catch(reject);
    });
  }

  async playCachedAudio(audioUrl) {
    return this.playAudio(audioUrl);
  }

  async preloadVocabulary(vocabularyList) {
    if (!this.isOnline) return;

    const promises = vocabularyList.map(word =>
      this.speak(word, { cache: true, priority: 'high' })
        .catch(() => {})
    );

    await Promise.allSettled(promises);
  }

  clearCache() {
    this.cache.clear();
  }
}

// Export singleton instance
const ttsService = new TTSService();
export default ttsService;